import { IAuthManagerInstantiator } from '../../controller/interface/auth';
import { IMediaManagerInstantiator } from '../../controller/interface/media';
import { IUserStateInstantiator } from '../../controller/interface/userstate';
import { IWorkspaceDBInstantiator } from '@monofe/apiservice/db/interface/instantiator';
import { ISession, ISessionActiveCallback, ISessionArgs } from '../interface/session';
import sessionDB, { ISessionData } from '../private/db';
// import { userState } from '@monofe/apiservice/controller';
import { IConfigAPI } from '@monofe/apiservice/interface';

function _queryDBForSessionData(name: string): Promise<ISessionData | undefined> {
    return sessionDB.load(name).catch(() => undefined);
}

function _updateSessionWithArgs(session: ISession, args: Partial<ISessionArgs>) {
    session.auth.apiUrl = args.apiUrl ?? session.auth.apiUrl;
    session.auth.username = args.apiUsername ?? session.auth.username;
    session.auth.password = args.apiPassword ?? session.auth.password;
}

function _flatten(session: ISession): ISessionData {
    return {
        name: session.name,
        userState: session.state.flatten() ?? '',
        auth: session.auth.serialize() ?? '',
    };
}

function _buildFromSessionData(
    config: IConfigAPI,
    authInst: IAuthManagerInstantiator,
    mediaInst: IMediaManagerInstantiator,
    userInst: IUserStateInstantiator,
    dbInst: IWorkspaceDBInstantiator,
    data: ISessionData,
): Promise<ISession> {
    return new Promise(async function (resolve, reject) {
        try {
            const authArgs = authInst.expand(data.auth);
            const auth = authInst.open(authArgs);

            const db = await dbInst.open({ name: data.name });

            const media = mediaInst.open(db, auth);
            const state = userInst.expand(data.userState);

            const session: ISession = {
                name: data.name,

                auth,
                db,
                media,
                state,

                save: function (this: ISession) {
                    sessionDB.save(this.name, _flatten(this));
                },
                remove: function (this: ISession) {
                    sessionDB.remove(data.name);
                },
            };

            resolve(session);
        } catch (error) {
            reject(error);
        }
    });
}

function _buildFromArgs(
    configAPI: IConfigAPI,
    authInst: IAuthManagerInstantiator,
    mediaInst: IMediaManagerInstantiator,
    userInst: IUserStateInstantiator,
    dbInst: IWorkspaceDBInstantiator,
    nameIn: string,
    args: ISessionArgs,
): Promise<ISession> {
    return new Promise(async function (resolve, reject) {
        try {
            const name = nameIn ?? args.apiUsername ?? configAPI.auth.username;

            const auth = authInst.open({
                apiUrl: args.apiUrl ?? configAPI.url,
                apiExtension: configAPI.extension.auth,
                clientId: configAPI.auth.clientId,
                clientSecret: configAPI.auth.clientSecret,
                grantType: configAPI.auth.grantType,
                username: args.apiUsername ?? configAPI.auth.username,
                password: args.apiPassword ?? configAPI.auth.password,
            });

            const db = await dbInst.open({ name });

            const media = mediaInst.open(db, auth);
            const state = userInst.open(name);

            const session: ISession = {
                name,

                auth,
                db,
                media,
                state,

                save: function (this: ISession) {
                    sessionDB.save(this.name, _flatten(this));
                },
                remove: function (this: ISession) {
                    sessionDB.remove(this.name);
                },
            };

            resolve(session);
        } catch (error) {
            reject(error);
        }
    });
}

const records: Record<string, ISession> = {};

function create(
    authInst: IAuthManagerInstantiator,
    mediaInst: IMediaManagerInstantiator,
    userInst: IUserStateInstantiator,
    dbInst: IWorkspaceDBInstantiator,
    configAPI: IConfigAPI,
    name: string,
    args: ISessionArgs,
): Promise<ISession> {
    const existing = records[name];

    if (existing) {
        return Promise.resolve(existing);
    }

    return new Promise(async function (resolve, reject) {
        try {
            const session = await _buildFromArgs(
                configAPI,
                authInst,
                mediaInst,
                userInst,
                dbInst,
                name,
                args,
            );

            records[name] = session;

            resolve(session);
        } catch (error) {
            reject(error);
        }
    });
}

function open(
    authInst: IAuthManagerInstantiator,
    mediaInst: IMediaManagerInstantiator,
    userInst: IUserStateInstantiator,
    dbInst: IWorkspaceDBInstantiator,
    configAPI: IConfigAPI,
    name: string,
): Promise<ISession> {
    const existing = records[name];

    if (existing) {
        return Promise.resolve(existing);
    }

    return new Promise(async function (resolve, reject) {
        try {
            /* ignore throw errors */
            const sessionData = await _queryDBForSessionData(name);

            if (!sessionData) {
                throw `Cannot find reference to saved session: ${name}`;
            }

            const sessionObj = await _buildFromSessionData(
                configAPI,
                authInst,
                mediaInst,
                userInst,
                dbInst,
                sessionData,
            );

            records[name] = sessionObj;

            resolve(sessionObj);
        } catch (error) {
            reject(error);
        }
    });
}

let activeSession: ISession | null = null;
let activeCallbacks: Array<ISessionActiveCallback> = [];

const active = () => activeSession;
const setActive = (sessionNew: ISession | null) => {
    activeSession = sessionNew;
    for (const cb of activeCallbacks) {
        cb(sessionNew);
    }
};
const subscribeActiveChanged = (callback: ISessionActiveCallback) => activeCallbacks.push(callback);
const unsubscribeActiveChanged = (callback: ISessionActiveCallback) =>
    (activeCallbacks = activeCallbacks.filter(cb => cb !== callback));

const module = {
    create,
    open,
    active,
    setActive,
    subscribeActiveChanged,
    unsubscribeActiveChanged,
};

export default module;
